import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Users, Fingerprint, Baby, Heart, ClipboardCheck, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { CountryCombobox, MultiCountryCombobox } from "@/components/CountryCombobox";
import { getCountries, checkEligibility, logEvent } from "@/lib/api";

const STORAGE = "atlas_intake_v1";
const MAX_CITIZENSHIPS = 3;
const AGE = ["18–30", "31–45", "46–60", "60+"];
const STEPS = [
  { key: "profile", label: "About you", icon: Users },
  { key: "citizenship", label: "Citizenship", icon: Fingerprint },
  { key: "parents", label: "Parents", icon: Baby },
  { key: "grandparents", label: "Grandparents", icon: Baby },
  { key: "marriage", label: "Marriage", icon: Heart },
  { key: "review", label: "Review", icon: ClipboardCheck },
];

// New shape: each ancestor holds an array of up to 3 citizenships. Real-world
// dual/tri-national parents are common (e.g. Argentine-Italian, US-Irish);
// prior single `citizenship` slot silently discarded the extras. Draft hydration
// below migrates the old shape forward.
const emptyAncestor = () => ({ birth_country: null, citizenships: [null] });

/** Normalize a possibly-legacy ancestor into the plural-citizenships shape,
 *  padding with nulls as needed but never exceeding MAX_CITIZENSHIPS. */
const normalizeAncestor = (a) => {
  if (!a || typeof a !== "object") return emptyAncestor();
  let list = Array.isArray(a.citizenships) ? [...a.citizenships] : [];
  if (list.length === 0 && a.citizenship) list = [a.citizenship];   // legacy singular → plural
  if (list.length === 0) list = [null];
  list = list.slice(0, MAX_CITIZENSHIPS);
  return { birth_country: a.birth_country || null, citizenships: list };
};

/** Ancestor row: 1 birth-country picker + 1..3 citizenship pickers with add /
 *  remove controls. Kept lightweight so the "known dad's country of birth,
 *  nothing else" happy path stays a single visual row. */
const AncestorRow = ({ countries, label, value, onChange, idxTest }) => {
  const cits = value.citizenships && value.citizenships.length > 0 ? value.citizenships : [null];
  const setCitizenship = (idx, c) => {
    const next = [...cits];
    next[idx] = c;
    onChange({ ...value, citizenships: next });
  };
  const addSlot = () => {
    if (cits.length >= MAX_CITIZENSHIPS) return;
    onChange({ ...value, citizenships: [...cits, null] });
  };
  const removeSlot = (idx) => {
    if (cits.length <= 1) {
      // Never fully empty the list; just clear the value.
      onChange({ ...value, citizenships: [null] });
      return;
    }
    const next = cits.filter((_, i) => i !== idx);
    onChange({ ...value, citizenships: next });
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-muted-foreground">{label} · country of birth</Label>
        <div className="mt-1">
          <CountryCombobox
            countries={countries}
            value={value.birth_country}
            placeholder="Country of birth"
            onChange={(c) => onChange({ ...value, birth_country: c })}
            testId={`${idxTest}-birth`}
          />
        </div>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">
          {label} · citizenship{cits.length > 1 ? "s" : ""}
        </Label>
        <div className="mt-1 space-y-2">
          {cits.map((c, i) => (
            // Stable key: slot ordinal within this ancestor. The list is
            // fixed-position (slot 0 is always the "primary" citizenship);
            // removing a middle slot compacts, which is fine because slots
            // carry no independent identity beyond their value.
            <div key={`${idxTest}-cit-${i}`} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <CountryCombobox
                  countries={countries}
                  value={c}
                  placeholder={i === 0 ? "Citizenship" : `Citizenship #${i + 1}`}
                  onChange={(v) => setCitizenship(i, v)}
                  testId={`${idxTest}-citizenship-${i}`}
                />
              </div>
              {cits.length > 1 && (
                <button
                  type="button"
                  aria-label={`Remove citizenship ${i + 1}`}
                  onClick={() => removeSlot(i)}
                  data-testid={`${idxTest}-citizenship-${i}-remove`}
                  className="h-9 w-9 shrink-0 rounded-md border text-muted-foreground hover:text-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--muted))] transition-colors"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {cits.length < MAX_CITIZENSHIPS && (
            <button
              type="button"
              onClick={addSlot}
              data-testid={`${idxTest}-add-citizenship`}
              className="text-xs text-[hsl(var(--atlas-navy))] hover:underline"
            >
              + Add another citizenship
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Intake() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const targetCountry = params.get("target");
  const [countries, setCountries] = useState([]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE);
      if (s) {
        const parsed = JSON.parse(s);
        // Forward-migrate legacy drafts (pre-multi-citizenship):
        //   parents/grandparents: {birth_country, citizenship: str}  →  {birth_country, citizenships: [str]}
        //   spouse_nationality: str                                  →  spouse_nationalities: [str]
        // We keep the legacy fields in state during editing (they're stripped
        // at submit time) so users mid-draft don't see values disappear if
        // they revisit an old tab. This is a purely additive migration.
        if (Array.isArray(parsed.parents)) parsed.parents = parsed.parents.map(normalizeAncestor);
        if (Array.isArray(parsed.grandparents)) parsed.grandparents = parsed.grandparents.map(normalizeAncestor);
        if (!Array.isArray(parsed.spouse_nationalities)) {
          parsed.spouse_nationalities = parsed.spouse_nationality ? [parsed.spouse_nationality] : [];
        }
        return parsed;
      }
    } catch (err) {
      console.warn("Intake: failed to restore saved draft, starting fresh.", err);
    }
    return {
      name: "", age_bracket: null, residences: [], nationalities: [], birth_country: null,
      parents: [emptyAncestor(), emptyAncestor()],
      grandparents: [emptyAncestor(), emptyAncestor(), emptyAncestor(), emptyAncestor()],
      marital_status: "single", spouse_nationalities: [],
    };
  });

  useEffect(() => { getCountries().then(setCountries).catch(() => toast.error("Failed to load countries")); }, []);
  useEffect(() => { localStorage.setItem(STORAGE, JSON.stringify(data)); }, [data]);

  // Fire `intake_started` exactly once per wizard session (a mount+unmount cycle).
  // Also carries the tax-explorer target when applicable, so the funnel view can
  // attribute intake-starts to the tax-explorer bridge.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    logEvent("intake_started", null, targetCountry ? { source: "tax_explorer", target_country: targetCountry } : {});
  }, [targetCountry]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  const canNext = useMemo(() => {
    if (step === 0) return !!data.age_bracket;
    if (step === 1) return data.nationalities.length > 0;
    return true;
  }, [step, data]);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Normalize before sending: strip null citizenship slots, drop empty
      // ancestors, and mirror plural fields back into the legacy singular
      // fields so the backend can accept the payload even if it hasn't been
      // redeployed (defensive belt-and-braces during the transition period).
      const cleanAncestor = (a) => {
        const cits = (a.citizenships || []).filter(Boolean);
        return { birth_country: a.birth_country || null, citizenships: cits, citizenship: cits[0] || null };
      };
      const parents = data.parents.map(cleanAncestor).filter((p) => p.birth_country || p.citizenships.length > 0);
      const grandparents = data.grandparents.map(cleanAncestor).filter((p) => p.birth_country || p.citizenships.length > 0);
      const spouseList = data.marital_status === "married" ? (data.spouse_nationalities || []).filter(Boolean) : [];
      const payload = {
        ...data,
        parents,
        grandparents,
        spouse_nationalities: spouseList,
        spouse_nationality: spouseList[0] || null,   // legacy mirror
      };
      const res = await checkEligibility(payload);
      logEvent("intake_completed", res.report_id, {
        nationalities_count: data.nationalities.length,
        has_parents: parents.length > 0,
        has_grandparents: grandparents.length > 0,
        marital_status: data.marital_status,
        // Extra funnel dimensions to observe multi-citizenship adoption.
        parents_multi_cit: parents.filter((p) => p.citizenships.length > 1).length,
        grandparents_multi_cit: grandparents.filter((p) => p.citizenships.length > 1).length,
        spouse_multi_cit: spouseList.length > 1,
      });
      navigate(`/teaser?report_id=${res.report_id}`);
    } catch (e) {
      toast.error("Something went wrong generating your results. Please try again.");
    } finally { setSubmitting(false); }
  };

  const GRAND_LABELS = ["Grandparent 1", "Grandparent 2", "Grandparent 3", "Grandparent 4"];

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10" data-testid="intake-wizard">
      {targetCountry && (
        <div
          data-testid="intake-target-hint"
          className="mb-4 flex items-start gap-2 rounded-xl border border-[hsl(var(--atlas-teal))]/30 bg-[hsl(var(--atlas-teal))]/5 px-4 py-3"
        >
          <Target className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--atlas-teal))]" />
          <p className="text-sm text-[hsl(var(--atlas-slate))]">
            Coming from the Tax Explorer for <span className="font-semibold text-[hsl(var(--atlas-navy))]">{targetCountry}</span>. Good instinct.
            The check below examines <span className="font-medium">every</span> country you could qualify for, not just this one. Fill out your ancestry and we&apos;ll surface {targetCountry}
            {" "}alongside anything else that matches.
          </p>
        </div>
      )}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))]">Check your eligibility</h1>
          <span className="text-sm text-muted-foreground tabular-nums">Step {step + 1} of {STEPS.length}</span>
        </div>
        <Progress value={pct} data-testid="intake-progress" className="h-2" />
        <div className="mt-3 hidden sm:flex items-center gap-1 flex-wrap">
          {STEPS.map((s, i) => (
            <button key={s.key} onClick={() => i <= step && setStep(i)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-colors ${i === step ? "bg-[hsl(var(--atlas-navy))] text-white" : i < step ? "bg-[hsl(var(--accent))] text-[hsl(var(--atlas-navy))]" : "text-muted-foreground"}`}>
              <s.icon className="h-3.5 w-3.5" /> {s.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="p-6 bg-white min-h-[340px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            {step === 0 && (
              <div className="space-y-6">
                <div>
                  <Label>Your name <span className="text-muted-foreground font-normal">(optional, for your report)</span></Label>
                  <input data-testid="intake-name" value={data.name} onChange={(e) => set({ name: e.target.value })}
                    className="mt-1 w-full rounded-lg border bg-white/80 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" placeholder="e.g. Alex Morgan" />
                </div>
                <div>
                  <Label>Your age bracket <span className="text-red-500">*</span></Label>
                  <RadioGroup value={data.age_bracket || ""} onValueChange={(v) => set({ age_bracket: v })} className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {AGE.map((a) => (
                      <Label key={a} htmlFor={`age-${a}`} className={`cursor-pointer rounded-lg border px-3 py-2.5 text-sm text-center ${data.age_bracket === a ? "border-[hsl(var(--atlas-navy))] bg-[hsl(var(--accent))]" : "bg-white/80"}`}>
                        <RadioGroupItem id={`age-${a}`} value={a} className="sr-only" data-testid={`intake-age-${a}`} />{a}
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
                <div>
                  <Label>Where do you currently hold long-term residence? <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <div className="mt-1"><MultiCountryCombobox countries={countries} values={data.residences} onChange={(v) => set({ residences: v })} placeholder="Add residence countries" testId="intake-residences" /></div>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <Label>Your current nationality/nationalities <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-muted-foreground mb-1">Include every passport you currently hold.</p>
                  <MultiCountryCombobox countries={countries} values={data.nationalities} onChange={(v) => set({ nationalities: v })} placeholder="Add your nationalities" testId="intake-nationalities-picker" />
                </div>
                <div>
                  <Label>Country of birth</Label>
                  <div className="mt-1"><CountryCombobox countries={countries} value={data.birth_country} onChange={(c) => set({ birth_country: c })} placeholder="Where were you born?" testId="intake-birth-country-picker" /></div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-5">
                <p className="text-sm text-muted-foreground">Your parents&apos; origins unlock the strongest descent claims. Fill in what you know; both fields are optional.</p>
                {data.parents.map((p, i) => (
                  <div key={i}>
                    <AncestorRow countries={countries} label={`Parent ${i + 1}`} value={p} idxTest={`intake-parent-${i}`}
                      onChange={(v) => { const a = [...data.parents]; a[i] = v; set({ parents: a }); }} />
                    {i < data.parents.length - 1 && <Separator className="mt-5" />}
                  </div>
                ))}
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5">
                <p className="text-sm text-muted-foreground">Grandparents are where the hidden EU passports hide (Italy, Ireland, Hungary, Romania…). Add any you know.</p>
                {data.grandparents.map((p, i) => (
                  <div key={i}>
                    <AncestorRow countries={countries} label={GRAND_LABELS[i]} value={p} idxTest={`intake-grandparent-${i}`}
                      onChange={(v) => { const a = [...data.grandparents]; a[i] = v; set({ grandparents: a }); }} />
                    {i < data.grandparents.length - 1 && <Separator className="mt-5" />}
                  </div>
                ))}
              </div>
            )}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <Label>Marital status</Label>
                  <RadioGroup value={data.marital_status} onValueChange={(v) => set({ marital_status: v })} className="mt-2 grid grid-cols-2 gap-2 max-w-xs">
                    {["single", "married"].map((m) => (
                      <Label key={m} className={`cursor-pointer rounded-lg border px-3 py-2.5 text-sm text-center capitalize ${data.marital_status === m ? "border-[hsl(var(--atlas-navy))] bg-[hsl(var(--accent))]" : "bg-white/80"}`}>
                        <RadioGroupItem value={m} className="sr-only" data-testid={`intake-marital-${m}`} />{m}
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
                {data.marital_status === "married" && (
                  <SpouseCitizenships
                    countries={countries}
                    values={data.spouse_nationalities || []}
                    onChange={(next) => set({ spouse_nationalities: next })}
                  />
                )}
              </div>
            )}
            {step === 5 && (
              <div className="space-y-4">
                <h3 className="font-display font-semibold text-lg text-[hsl(var(--atlas-navy))]">Review your details</h3>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <ReviewItem label="Name" value={data.name || "—"} />
                  <ReviewItem label="Age bracket" value={data.age_bracket || "—"} />
                  <ReviewItem label="Nationalities" value={data.nationalities.join(", ") || "—"} />
                  <ReviewItem label="Born in" value={data.birth_country || "—"} />
                  <ReviewItem label="Residence" value={data.residences.join(", ") || "—"} />
                  <ReviewItem
                    label="Marital status"
                    value={
                      data.marital_status +
                      ((data.spouse_nationalities || []).filter(Boolean).length > 0
                        ? ` (spouse: ${(data.spouse_nationalities || []).filter(Boolean).join(", ")})`
                        : "")
                    }
                  />
                  <ReviewItem label="Parents" value={reviewAncestors(data.parents)} />
                  <ReviewItem label="Grandparents" value={reviewAncestors(data.grandparents)} />
                </div>
                <p className="text-xs text-muted-foreground">We store the minimum needed to generate your report. See our <a href="/privacy" className="underline">privacy policy</a>.</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </Card>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="intake-back-button">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="intake-next-button"
            className="bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white">
            Next <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={submitting} data-testid="intake-submit-button"
            className="bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white">
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing…</> : <>See my results <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        )}
      </div>
    </div>
  );
}

const ReviewItem = ({ label, value }) => (
  <div className="rounded-lg border bg-white/70 px-3 py-2">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-[hsl(var(--atlas-navy))] font-medium">{value}</div>
  </div>
);

/** Summarize an ancestor list for the review step: one segment per non-empty
 *  ancestor, showing all their citizenships (falls back to birth_country if
 *  none). Returns an em-dash if no ancestor has anything filled in. */
const reviewAncestors = (list) => {
  const segments = (list || [])
    .map((a) => {
      const cits = (a.citizenships || []).filter(Boolean);
      if (cits.length > 0) return cits.join("/");
      if (a.birth_country) return `born in ${a.birth_country}`;
      return null;
    })
    .filter(Boolean);
  return segments.length > 0 ? segments.join(", ") : "—";
};

/** Spouse citizenships editor: identical UX to the citizenship half of
 *  AncestorRow but rendered as a standalone block. Extracted so it can be
 *  reused independently from the two-column ancestor layout. */
const SpouseCitizenships = ({ countries, values, onChange }) => {
  const list = values && values.length > 0 ? values : [null];
  const set = (idx, c) => {
    const next = [...list];
    next[idx] = c;
    // Keep null slots in place — the user may re-select. Nulls are stripped at
    // submit time. Preserving them avoids a jumpy UX where clearing #2 collapses
    // #3 into #2's position mid-edit.
    onChange(next);
  };
  const add = () => {
    if (list.length >= MAX_CITIZENSHIPS) return;
    onChange([...list, null]);
  };
  const remove = (idx) => {
    if (list.length <= 1) { onChange([]); return; }
    onChange(list.filter((_, i) => i !== idx));
  };
  return (
    <div>
      <Label>Your spouse&apos;s nationality</Label>
      <div className="mt-1 max-w-md space-y-2">
        {list.map((c, i) => (
          <div key={`spouse-cit-${i}`} className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <CountryCombobox
                countries={countries}
                value={c}
                placeholder={i === 0 ? "Spouse nationality" : `Spouse nationality #${i + 1}`}
                onChange={(v) => set(i, v)}
                testId={`intake-spouse-nationality-${i}`}
              />
            </div>
            {list.length > 1 && (
              <button
                type="button"
                aria-label={`Remove spouse nationality ${i + 1}`}
                onClick={() => remove(i)}
                data-testid={`intake-spouse-nationality-${i}-remove`}
                className="h-9 w-9 shrink-0 rounded-md border text-muted-foreground hover:text-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--muted))] transition-colors"
              >
                ×
              </button>
            )}
          </div>
        ))}
        {list.length < MAX_CITIZENSHIPS && (
          <button
            type="button"
            onClick={add}
            data-testid="intake-spouse-add-citizenship"
            className="text-xs text-[hsl(var(--atlas-navy))] hover:underline"
          >
            + Add another citizenship
          </button>
        )}
      </div>
    </div>
  );
};
